import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../services/firestoreService';

const PROCEDURE_CATEGORIES = [
  'Tejidos Blandos',
  'Ortopedia',
  'Dental',
  'Oftalmología',
  'Neurología',
  'Oncología',
  'Emergencia',
  'Otro'
];

const COMPLEXITY_LEVELS = ['Baja', 'Media', 'Alta'];

const initialFormState = {
  id: '',
  name: '',
  category: '',
  species: [],
  description: '',
  duration: 60,
  complexity: 'Media',
  equipment: '',
  materials: '',
  active: true
};

const ManageProcedures = () => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProcedure, setCurrentProcedure] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadProcedures();
  }, []);

  const loadProcedures = async () => {
    try {
      setLoading(true);
      const data = await getCollection('surgicalProcedures');
      // Agregar firestoreId para identificar el documento, mantener id personalizado separado
      const proceduresWithFirestoreId = data.map(proc => ({
        ...proc,
        firestoreId: proc.id, // ID del documento en Firestore
        procedureId: proc.id || proc.procedureId // ID personalizado del procedimiento
      }));
      setProcedures(proceduresWithFirestoreId.sort((a, b) => a.category.localeCompare(b.category)));
    } catch (error) {
      console.error('Error loading procedures:', error);
      setSnackbar({ open: true, message: 'Error al cargar procedimientos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (procedure = null) => {
    if (procedure) {
      setEditMode(true);
      setCurrentProcedure(procedure);
      setFormData({
        id: procedure.procedureId || procedure.id || '',
        name: procedure.name || '',
        category: procedure.category || '',
        species: procedure.species || [],
        description: procedure.description || '',
        duration: procedure.duration || 60,
        complexity: procedure.complexity || 'Media',
        equipment: Array.isArray(procedure.equipment) ? procedure.equipment.join('\n') : procedure.equipment || '',
        materials: Array.isArray(procedure.materials) ? procedure.materials.join('\n') : procedure.materials || '',
        active: procedure.active !== false
      });
    } else {
      setEditMode(false);
      setCurrentProcedure(null);
      setFormData(initialFormState);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentProcedure(null);
    setFormData(initialFormState);
  };

  const handleViewProcedure = (procedure) => {
    setCurrentProcedure(procedure);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setCurrentProcedure(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSpeciesChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      species: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSave = async () => {
    try {
      // Validaciones
      if (!formData.name || !formData.category) {
        setSnackbar({ open: true, message: 'Complete los campos obligatorios', severity: 'error' });
        return;
      }

      const procedureData = {
        id: formData.id || `custom_${Date.now()}`,
        name: formData.name,
        category: formData.category,
        species: formData.species,
        description: formData.description,
        duration: parseInt(formData.duration) || 60,
        complexity: formData.complexity,
        equipment: formData.equipment.split('\n').filter(item => item.trim()),
        materials: formData.materials.split('\n').filter(item => item.trim()),
        active: formData.active,
        updatedAt: new Date()
      };

      if (editMode && currentProcedure) {
        // Actualizar procedimiento existente
        await updateDocument('surgicalProcedures', currentProcedure.firestoreId, procedureData);
        setSnackbar({ open: true, message: 'Procedimiento actualizado exitosamente', severity: 'success' });
      } else {
        // Crear nuevo procedimiento
        procedureData.createdAt = new Date();
        await addDocument('surgicalProcedures', procedureData);
        setSnackbar({ open: true, message: 'Procedimiento creado exitosamente', severity: 'success' });
      }

      handleCloseDialog();
      loadProcedures();
    } catch (error) {
      console.error('Error saving procedure:', error);
      setSnackbar({ open: true, message: 'Error al guardar procedimiento', severity: 'error' });
    }
  };

  const handleDelete = async (procedure) => {
    if (!window.confirm(`¿Está seguro de eliminar el procedimiento "${procedure.name}"?`)) {
      return;
    }

    try {
      await deleteDocument('surgicalProcedures', procedure.firestoreId);
      setSnackbar({ open: true, message: 'Procedimiento eliminado exitosamente', severity: 'success' });
      loadProcedures();
    } catch (error) {
      console.error('Error deleting procedure:', error);
      setSnackbar({ open: true, message: 'Error al eliminar procedimiento', severity: 'error' });
    }
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'Baja': return 'success';
      case 'Media': return 'warning';
      case 'Alta': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">
            Gestión de Procedimientos Quirúrgicos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {procedures.length} procedimientos
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Procedimiento
        </Button>
      </Box>
      {snackbar.open && (
        <Alert severity={snackbar.severity} sx={{ mb: 2 }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Categoría</strong></TableCell>
              <TableCell><strong>Especies</strong></TableCell>
              <TableCell><strong>Duración</strong></TableCell>
              <TableCell><strong>Complejidad</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {procedures.map((procedure) => (
              <TableRow key={procedure.firestoreId} hover>
                <TableCell>{procedure.name}</TableCell>
                <TableCell>
                  <Chip label={procedure.category} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>
                  {procedure.species?.join(', ') || 'N/A'}
                </TableCell>
                <TableCell>{procedure.duration} min</TableCell>
                <TableCell>
                  <Chip
                    label={procedure.complexity}
                    size="small"
                    color={getComplexityColor(procedure.complexity)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver detalles">
                    <IconButton size="small" color="info" onClick={() => handleViewProcedure(procedure)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(procedure)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" color="error" onClick={() => handleDelete(procedure)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {procedures.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No hay procedimientos registrados. Haga click en "Nuevo Procedimiento" para agregar uno.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Dialog para Crear/Editar Procedimiento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Editar Procedimiento' : 'Nuevo Procedimiento'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                label="ID del Procedimiento *"
                name="id"
                value={formData.id}
                onChange={handleInputChange}
                fullWidth
                helperText="Identificador único (ej: tb_007, ort_006)"
                disabled={editMode}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                label="Nombre del Procedimiento *"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                select
                label="Categoría *"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                fullWidth
                required
              >
                {PROCEDURE_CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                select
                label="Especies *"
                name="species"
                value={formData.species}
                onChange={handleSpeciesChange}
                fullWidth
                SelectProps={{ multiple: true }}
                helperText="Puede seleccionar múltiples"
              >
                <MenuItem value="Canino">Canino</MenuItem>
                <MenuItem value="Felino">Felino</MenuItem>
              </TextField>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                label="Duración (minutos) *"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                select
                label="Complejidad *"
                name="complexity"
                value={formData.complexity}
                onChange={handleInputChange}
                fullWidth
                required
              >
                {COMPLEXITY_LEVELS.map(level => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Descripción del Procedimiento"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Equipamiento e Instrumental"
                name="equipment"
                value={formData.equipment}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
                helperText="Ingrese cada item en una línea separada"
                placeholder="Set de cirugía general&#10;Pinzas de Allis&#10;Bisturí&#10;..."
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Materiales e Insumos"
                name="materials"
                value={formData.materials}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
                helperText="Ingrese cada item en una línea separada"
                placeholder="Suturas absorbibles 2-0&#10;Gasas estériles&#10;Guantes&#10;..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editMode ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog para Ver Detalles */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalles del Procedimiento
        </DialogTitle>
        <DialogContent>
          {currentProcedure && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="h6" color="primary">
                    {currentProcedure.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {currentProcedure.procedureId || currentProcedure.id}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">Categoría:</Typography>
                  <Chip label={currentProcedure.category} color="primary" size="small" />
                </Grid>

                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">Complejidad:</Typography>
                  <Chip
                    label={currentProcedure.complexity}
                    color={getComplexityColor(currentProcedure.complexity)}
                    size="small"
                  />
                </Grid>

                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">Especies:</Typography>
                  <Typography variant="body1">
                    {currentProcedure.species?.join(', ') || 'N/A'}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">Duración:</Typography>
                  <Typography variant="body1">{currentProcedure.duration} minutos</Typography>
                </Grid>

                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Descripción:</Typography>
                  <Typography variant="body1">{currentProcedure.description || 'Sin descripción'}</Typography>
                </Grid>

                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Equipamiento e Instrumental:</Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    {(Array.isArray(currentProcedure.equipment)
                      ? currentProcedure.equipment
                      : currentProcedure.equipment?.split('\n') || []
                    ).map((item, index) => (
                      <li key={index}>
                        <Typography variant="body2">{item}</Typography>
                      </li>
                    ))}
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Materiales e Insumos:</Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    {(Array.isArray(currentProcedure.materials)
                      ? currentProcedure.materials
                      : currentProcedure.materials?.split('\n') || []
                    ).map((item, index) => (
                      <li key={index}>
                        <Typography variant="body2">{item}</Typography>
                      </li>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Cerrar</Button>
          <Button
            onClick={() => {
              handleCloseViewDialog();
              handleOpenDialog(currentProcedure);
            }}
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
          >
            Editar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageProcedures;
